// ============================================================
// IntegratedSearchPage.js  (루트 — 상태 연결·조립)
// 하위 컴포넌트는 searchComponents/ 로 분리.
//   Icons          : 공통 아이콘 + ROW_HEIGHT
//   FilterRail     : 왼쪽 필터 레일 (CollapsedRail/FilterRail/FieldControl/
//                    MutationSelect/CnvSelect/ChipSelect/HgvspPanel)
//   QuerySentence  : 상단 필터 문장
//   ResultsTable   : 결과 테이블 (NgsBadges/IC50Cell/FusionPill)
//   ActionFooter   : 하단 액션바
//   DetailDrawer   : 상세 Drawer + 약물반응(IC50) 탭
//   CohortModal    : 코호트 관리 모달
// 데이터/계산은 백엔드 API(useSearchData)에서 수행.
// ============================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./IntegratedSearchPage.module.css";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { filterSummaryLines, EXPORT_LIST_COLS } from "./data/searchData";
import { useSearchData } from "./hooks/useSearchData";
import { Icon } from "./searchComponents/Icons";
import { CollapsedRail, FilterRail } from "./searchComponents/FilterRail";
import { QuerySentence } from "./searchComponents/QuerySentence";
import { ResultsTable } from "./searchComponents/ResultsTable";
import { ActionFooter } from "./searchComponents/ActionFooter";
import { DetailDrawer } from "./searchComponents/DetailDrawer";
import { CohortModal } from "./searchComponents/CohortModal";

function IntegratedSearchPage() {
    const { t } = useTranslation();
    const search = useSearchData();
    const {
        loading, error, dataAsOf, dataStale, rows, filteredCount,
        page, totalPages, pageSize, setPage, setPageSize,
        chips, ranges, sort,
        toggleChip, setRange, clearAll, applyCohort, toggleSort,
        totalActive, totalSamples,
        cohorts, addCohort, removeCohort,
        selectedDrug, setSelectedDrug,
        dynamicSubtypes, dynamicMutGenes, dynamicCnvGenes, dynamicDrug, filterOptions,
        dynamicTxDrug, dynamicMutRepGenes, dynamicOrgans,
        mutState, cycleMutation, hgvsp, toggleHgvsp, includedGenes,
        cnv, addCnvGene, setCnvType, removeCnvGene,
        // 선택은 "필터된 전체" 단위만 쓴다(페이지 단위 선택은 ResultsTable 에서 제거됨).
        selectedKeys, toggleOne, selectAllFiltered, clearSelection,
        allFilteredSelected, selectedCount,
        fetchExportData, fetchDetail,
    } = search;

    const [railCollapsed, setRailCollapsed] = useState(false);
    const [openGroups, setOpenGroups] = useState({ sample: true, molecular: true, drug: false, pairing: false, patient: false });
    const [focusedKey, setFocusedKey] = useState(null);
    const [cohortModalOpen, setCohortModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    const focused = focusedKey ? rows.find((r) => r.groupKey === focusedKey) : null;

    /* ---------- Export xlsx ----------
     * 첫 시트 = 화면 목록과 같은 컬럼 + 맨 위에 적용한 필터 조건.
     * 그 뒤로 기존 WES / RNA / Mutation Detail 시트가 따라온다. */
    const handleExport = async () => {
        if (selectedCount === 0) return;
        setExporting(true);
        try {
            const data = await fetchExportData();
            if (!data) return;
            const wb = utils.book_new();

            // 1) 검색 결과 시트 (첫 시트)
            const lines = filterSummaryLines({ chips, mutState, cnv, selectedDrug }, t);
            const aoa = [];
            aoa.push([t('CRO 통합검색 결과')]);
            aoa.push([t('내보낸 시각'), new Date().toLocaleString()]);
            aoa.push([t('적용한 필터')]);
            if (lines.length) lines.forEach((l) => aoa.push(["", l]));
            else aoa.push(["", t('적용한 필터 없음')]);
            aoa.push([]);
            aoa.push(EXPORT_LIST_COLS.map((c) => t(c.label)));
            // 고른 게 없으면 값 대신 "안 골랐다"고 적는다(화면 목록과 같은 규칙).
            const noMut = includedGenes.length === 0 ? t('Mutation 필터 미선택') : null;
            const noDrug = (chips.drug || []).length === 0 && !selectedDrug ? t('약물 미선택') : null;
            (data.list || []).forEach((r) => aoa.push(EXPORT_LIST_COLS.map((c) => {
                if (c.key === "Mutation" && noMut) return noMut;
                if (c.key === "IC50" && noDrug) return noDrug;
                return r[c.key] == null ? "" : r[c.key];
            })));
            utils.book_append_sheet(wb, utils.aoa_to_sheet(aoa), "Search Result");

            const wes = data.wes || [];
            const rna = data.rna || [];
            const mutationDetail = data.mutationDetail || [];
            if (wes.length) utils.book_append_sheet(wb, utils.json_to_sheet(wes), "WES Summary");
            if (rna.length) utils.book_append_sheet(wb, utils.json_to_sheet(rna), "RNA Summary");
            if (mutationDetail.length) utils.book_append_sheet(wb, utils.json_to_sheet(mutationDetail), "Mutation Detail");
            if (!wes.length && !rna.length) utils.book_append_sheet(wb, utils.json_to_sheet([{ note: t('선택한 샘플에 데이터가 없습니다.') }]), "Info");
            const out = write(wb, { bookType: "xlsx", type: "array" });
            const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
            saveAs(new Blob([out], { type: "application/octet-stream" }), `CRO_Integrated_Export_${ts}.xlsx`);
        } catch (e) {
            alert(t('내보내기 실패') + ": " + e.message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className={styles.rightTab}>
            <div className={styles.topMenu}>
                <div className={styles.curOrganoid}><Icon.Search />&nbsp;{t('통합검색')}</div>
                <div className={styles.topActions}>
                </div>
            </div>

            <div className={styles.rightTabBottomBox}>
                <div className={styles.innerSplit}>
                    {railCollapsed ? (
                        <CollapsedRail onExpand={() => setRailCollapsed(false)} totalActive={totalActive}
                            resultCount={filteredCount} totalSamples={totalSamples} />
                    ) : (
                        <FilterRail
                            openGroups={openGroups} setOpenGroups={setOpenGroups}
                            chips={chips} ranges={ranges} toggleChip={toggleChip} setRange={setRange}
                            totalActive={totalActive} clearAll={clearAll}
                            cohorts={cohorts} applyCohort={applyCohort}
                            onManageCohorts={() => setCohortModalOpen(true)}
                            onCollapse={() => setRailCollapsed(true)}
                            resultCount={filteredCount} totalSamples={totalSamples}
                            dynamicSubtypes={dynamicSubtypes}
                            dynamicMutGenes={dynamicMutGenes} dynamicCnvGenes={dynamicCnvGenes}
                            dynamicDrug={dynamicDrug}
                            dynamicTxDrug={dynamicTxDrug} dynamicMutRepGenes={dynamicMutRepGenes}
                            dynamicOrgans={dynamicOrgans}
                            mutState={mutState} cycleMutation={cycleMutation}
                            hgvsp={hgvsp} toggleHgvsp={toggleHgvsp} includedGenes={includedGenes}
                            hgvspByGene={filterOptions.hgvspByGene || {}}
                            cnv={cnv} addCnvGene={addCnvGene} setCnvType={setCnvType} removeCnvGene={removeCnvGene}
                        />
                    )}

                    <main className={styles.mainCol}>
                        <QuerySentence chips={chips} toggleChip={toggleChip} totalSamples={totalSamples}
                            selectedDrug={selectedDrug} mutState={mutState} cycleMutation={cycleMutation} />
                        {error && <div className={styles.errorBar}>{t('데이터를 불러오지 못했습니다')}: {error}</div>}
                        {loading ? (
                            <div className={styles.tableLoading}>{t('불러오는 중…')}</div>
                        ) : (
                            <ResultsTable
                                rows={rows} selectedKeys={selectedKeys}
                                toggleOne={toggleOne} mutIncludeGenes={includedGenes}
                                dataAsOf={dataAsOf} dataStale={dataStale}
                                focusedKey={focusedKey} setFocusedKey={setFocusedKey}
                                sort={sort} toggleSort={toggleSort}
                                page={page} totalPages={totalPages} pageSize={pageSize}
                                filteredCount={filteredCount} setPage={setPage} setPageSize={setPageSize}
                                selectedDrug={selectedDrug} setSelectedDrug={setSelectedDrug} drugOptions={dynamicDrug}
                                allFilteredSelected={allFilteredSelected}
                                selectAllFiltered={selectAllFiltered} clearSelection={clearSelection}
                                selectedCount={selectedCount}
                            />
                        )}
                        <ActionFooter selectedCount={selectedCount} filteredCount={filteredCount}
                            onClear={clearSelection} onExport={handleExport} exporting={exporting} />
                    </main>

                    {focused && (
                        <DetailDrawer group={focused} selectedDrug={selectedDrug} setSelectedDrug={setSelectedDrug}
                            fetchDetail={fetchDetail} onClose={() => setFocusedKey(null)} />
                    )}
                </div>
            </div>

            {cohortModalOpen && (
                <CohortModal
                    cohorts={cohorts} chips={chips}
                    onAdd={addCohort} onRemove={removeCohort}
                    onApply={(c) => { applyCohort(c); setCohortModalOpen(false); }}
                    onClose={() => setCohortModalOpen(false)}
                />
            )}
        </div>
    );
}

export default IntegratedSearchPage;